import React, { useEffect } from "react";
import { Col, Row, Select } from "antd";
import { Controller } from "react-hook-form";
import { PhoneOutlined, MailOutlined, GlobalOutlined } from "@ant-design/icons";
import { Country, State } from "country-state-city";
import ProfileForm from "../../../../../components/profile-form/profile-form";

const InstitutionContact = ({
  control,
  isEditMode,
  getValues,
  resetField,
  setValue,
  watch,
  trigger
}) => {
  const physicalAddress = watch("physical_address");
  const lat = watch("lat");
  const lng = watch("lng");

  useEffect(() => {
    if (physicalAddress) {
      trigger("physical_address");
    }
  }, [lat, lng]);

  return (
    <Row style={{ margin: "20px 0" }} gutter={[32, 16]}>
      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="physical_address"
          control={control}
          rules={{
            required: "Physical Address is required",
            validate: () => {
              if (!lat || !lng) {
                return "Please select a valid address from the dropdown";
              }
              return true;
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <ProfileForm.AutoComplete
              isEdit={isEditMode}
              inputLabel="Physical Address"
              placeholder="Type physical address"
              required
              value={value}
              onChange={(val) => {
                onChange(val);
                setValue("lat", "");
                setValue("lng", "");
              }}
              onSelect={(d) => {
                setValue("lat", d.lat);
                setValue("lng", d.lng);
              }}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="postal_address"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.TextInput
              isEdit={isEditMode}
              inputLabel="Postal Address"
              placeholder="Type postal address"
              value={field.value}
              onChange={field.onChange}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="country"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.Select
              inputLabel="Country"
              placeholder="--Select--"
              required
              isEdit={isEditMode}
              value={field.value}
              onChange={(...args) => {
                resetField("province");
                field.onChange(...args);
              }}
              status={error ? "error" : ""}
            >
              {Country.getAllCountries().map((item) => (
                <Select.Option key={item.isoCode} value={item.name}>
                  {item.name}
                </Select.Option>
              ))}
            </ProfileForm.Select>
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="province"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.Select
              inputLabel="Province"
              required
              placeholder="--Select--"
              isEdit={isEditMode}
              value={field.value}
              onChange={field.onChange}
              status={error ? "error" : ""}
            >
              {State.getStatesOfCountry(
                Country.getAllCountries().find((i) => i.name === getValues("country"))?.isoCode
              )?.map((item) => (
                <Select.Option key={item.isoCode} value={item.name}>
                  {item.name}
                </Select.Option>
              ))}
            </ProfileForm.Select>
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="principal_phone_number"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.TextInput
              isEdit={isEditMode}
              inputLabel="Head/Principal's Phone"
              type="number"
              placeholder="Enter Phone Number"
              required
              value={field.value}
              onChange={field.onChange}
              prefix={<PhoneOutlined style={{ color: "#999999", paddingRight: 4 }} />}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="principal_email_address"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.TextInput
              isEdit={isEditMode}
              inputLabel="Email of Head/Principal"
              type="email"
              placeholder="Enter Email"
              value={field.value}
              onChange={field.onChange}
              prefix={<MailOutlined style={{ color: "#999999", paddingRight: 4 }} />}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="deputy_principal_phone_number"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.TextInput
              isEdit={isEditMode}
              inputLabel="Phone of Deputy Head/Principal"
              type="number"
              placeholder="Enter Phone Number"
              value={field.value}
              onChange={field.onChange}
              prefix={<PhoneOutlined style={{ color: "#999999", paddingRight: 4 }} />}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="deputy_principal_email_address"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.TextInput
              isEdit={isEditMode}
              inputLabel="Email of Deputy Head/Principal"
              type="email"
              placeholder="Enter Email"
              value={field.value}
              onChange={field.onChange}
              prefix={<MailOutlined style={{ color: "#999999", paddingRight: 4 }} />}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="school_phone_number"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.TextInput
              isEdit={isEditMode}
              inputLabel="School Phone Number"
              type="number"
              placeholder="Enter Phone Number"
              required
              value={field.value}
              onChange={field.onChange}
              prefix={<PhoneOutlined style={{ color: "#999999", paddingRight: 4 }} />}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="school_email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.TextInput
              isEdit={isEditMode}
              inputLabel="School Email"
              type="email"
              placeholder="Enter Email"
              value={field.value}
              onChange={field.onChange}
              prefix={<MailOutlined style={{ color: "#999999", paddingRight: 4 }} />}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>

      <Col xs={24} sm={24} md={24} lg={12}>
        <Controller
          name="school_website_url"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <ProfileForm.TextInput
              isEdit={isEditMode}
              inputLabel="Website URL"
              type="url"
              placeholder="Enter website URL"
              value={field.value}
              onChange={field.onChange}
              prefix={<GlobalOutlined style={{ color: "#999999", paddingRight: 4 }} />}
              status={error ? "error" : ""}
            />
          )}
        />
      </Col>
    </Row>
  );
};

export default InstitutionContact;
